import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // «Accedi come condomino»: il token temporaneo sta solo in questa scheda (sessionStorage): l'amministratore resta
  // collegato nelle altre schede e, chiusa la scheda, l'accesso come condomino finisce da solo.
  const leggiImp = () => { try { return sessionStorage.getItem('imp_token'); } catch (e) { return null; } };
  const [impToken, setImpToken] = useState(leggiImp);
  const [token, setToken] = useState(() => leggiImp() || localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [condominiums, setCondominiums] = useState([]);
  const [selectedCondominium, setSelectedCondominium] = useState(
    JSON.parse(localStorage.getItem('selectedCondominium') || 'null')
  );
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Piano + feature flags effettivi del workspace (fonte di verità per il gating UI).
  // Es: { custom_branding: false, api_access: false, priority_support: false, ... }
  const [planFeatures, setPlanFeatures] = useState({});
  const [planCode, setPlanCode] = useState(null);

  // Set axios default header and fetch user on mount
  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        try {
          await fetchUser();
          await fetchCondominiums();
          await fetchPlanFeatures();
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Auth init failed:', error);
          logout();
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []); // Only run on mount

  const fetchUser = async () => {
    const response = await axios.get(`${API}/auth/me`);
    setUser(response.data);
    return response.data;
  };

  // Recupera i feature flag del piano dal backend (/api/workspace/status).
  // Non deve far fallire l'autenticazione se l'endpoint risponde male:
  // in caso di errore lasciamo le features vuote (gating prudente lato server).
  const fetchPlanFeatures = async () => {
    try {
      const response = await axios.get(`${API}/workspace/status`);
      const data = response.data || {};
      setPlanFeatures(data.features || {});
      setPlanCode(data.plan_code || null);
      return data.features || {};
    } catch (error) {
      console.error('Failed to fetch plan features:', error);
      setPlanFeatures({});
      return {};
    }
  };

  // Helper comodo per i componenti: hasFeature('custom_branding') -> bool
  const hasFeature = (key) => Boolean(planFeatures?.[key]);

  const fetchCondominiums = async () => {
    try {
      const response = await axios.get(`${API}/condominiums`);
      const list = response.data || [];
      setCondominiums(list);
      // Riconcilia SEMPRE la selezione con i dati freschi dal server (auto-guarigione):
      // - se la selezione salvata esiste ancora, la riaggancia per avere ruolo/dettagli aggiornati
      //   (evita il caso "Nessun ruolo" da localStorage stantio);
      // - altrimenti auto-seleziona il primo condominio;
      // - se non ci sono condomìni, azzera la selezione.
      const stored = JSON.parse(localStorage.getItem('selectedCondominium') || 'null');
      const match = stored && list.find(
        (c) => c.condominium?.id === stored.condominium?.id
      );
      if (match) {
        selectCondominium(match);
      } else if (list.length > 0) {
        selectCondominium(list[0]);
      } else {
        setSelectedCondominium(null);
        localStorage.removeItem('selectedCondominium');
      }
      return list;
    } catch (error) {
      console.error('Failed to fetch condominiums:', error);
      return [];
    }
  };

  const login = async (email, password) => {
    // Step 1: Get token
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token } = response.data;
    
    try { sessionStorage.removeItem('avviso_quote_visto'); } catch (e) { /* niente */ }
    // Step 2: Store token
    localStorage.setItem('token', access_token);
    setToken(access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Step 3: Fetch user data immediately (wait for it!)
    const userData = await fetchUser();
    
    // Step 4: Fetch condominiums
    await fetchCondominiums();

    // Step 4b: Fetch plan feature flags (gating)
    await fetchPlanFeatures();

    // Step 5: Mark as authenticated
    setIsAuthenticated(true);
    
    return { ...response.data, user: userData };
  };

  const register = async (email, password, full_name, fiscal_code) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name,
      fiscal_code
    });
    return response.data;
  };

  // L'amministratore apre l'utenza di un condomino (token breve ottenuto da /accessi/{id}/accedi-come):
  // vale solo in questa scheda. Ricarica la pagina cosi' parte da zero con l'identita' del condomino.
  const entraComeCondomino = (impAccessToken) => {
    try { sessionStorage.setItem('imp_token', impAccessToken); sessionStorage.removeItem('avviso_quote_visto'); } catch (e) { return; }
    window.location.assign('/dashboard');
  };

  // Chiude «accedi come» e torna all'amministratore (il suo token e' ancora in localStorage).
  const tornaAdmin = () => {
    try { sessionStorage.removeItem('imp_token'); } catch (e) { /* niente */ }
    window.location.assign('/accessi-condomini');
  };

  const logout = () => {
    if (impToken) { tornaAdmin(); return; }
    localStorage.removeItem('token');
    localStorage.removeItem('selectedCondominium');
    try { sessionStorage.removeItem('avviso_quote_visto'); } catch (e) { /* niente */ }
    setToken(null);
    setUser(null);
    setCondominiums([]);
    setSelectedCondominium(null);
    setPlanFeatures({});
    setPlanCode(null);
    setIsAuthenticated(false);
    delete axios.defaults.headers.common['Authorization'];
  };

  const selectCondominium = (condoWithRole) => {
    setSelectedCondominium(condoWithRole);
    if (!impToken) localStorage.setItem('selectedCondominium', JSON.stringify(condoWithRole));
  };

  const refreshCondominiums = async () => {
    await fetchCondominiums();
  };

  // Ricarica i dati dell'utente corrente (es. dopo cambio password / consenso privacy).
  const refreshUser = async () => {
    try {
      return await fetchUser();
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    condominiums,
    selectedCondominium,
    // D25 — il ruolo sul condominio scelto. Cinque pagine (Documenti, Personale,
    // Dettaglio dipendente, Consulente, Portale dipendente) lo leggevano da qui
    // con `const { userRole } = useAuth()`, ma il contesto non lo esponeva:
    // restava undefined e ogni controllo tipo `userRole === 'ADMIN'` era falso.
    // Risultato: nell'archivio i tasti carica, elimina e condividi non
    // comparivano a nessuno, nemmeno all'amministratore.
    userRole: selectedCondominium?.role || 'RESIDENT',
    planFeatures,
    planCode,
    hasFeature,
    accessoComeCondomino: Boolean(impToken),
    entraComeCondomino,
    tornaAdmin,
    login,
    register,
    logout,
    selectCondominium,
    refreshCondominiums,
    refreshUser,
    refreshPlanFeatures: fetchPlanFeatures
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};